/**
 * PWA Install Banner Component
 * G2eGente - Marketplace de Recrutamento Ético
 */
import React, { useState, useEffect } from 'react';
import { X, Download, Wifi, WifiOff, RefreshCw } from 'lucide-react';
import { Button } from '../components/ui/button';
import usePWA from './usePWA';

// Install Banner - Shows when app can be installed
export function PWAInstallBanner() {
  const { canBeInstalled, install, isInstalled } = usePWA();
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if user already dismissed
    const wasDismissed = localStorage.getItem('pwa-install-dismissed');
    if (wasDismissed) {
      const dismissedTime = parseInt(wasDismissed, 10);
      // Show again after 7 days
      if (Date.now() - dismissedTime < 7 * 24 * 60 * 60 * 1000) {
        setDismissed(true);
      }
    }
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-install-dismissed', Date.now().toString());
  };

  const handleInstall = async () => {
    const success = await install();
    if (success) {
      localStorage.removeItem('pwa-install-dismissed');
    }
  };

  if (!canBeInstalled || dismissed || isInstalled) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 bg-gradient-to-r from-cyan-600 to-cyan-500 text-white rounded-2xl p-4 shadow-lg z-50 animate-in slide-in-from-bottom-4">
      <button 
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 text-white/80 hover:text-white"
      >
        <X className="w-5 h-5" />
      </button>
      
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <Download className="w-6 h-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-sm">Instale o G2eGente</h3>
          <p className="text-xs text-white/80">Acesso rápido e notificações</p>
        </div>
        <Button 
          onClick={handleInstall}
          className="bg-white text-cyan-600 hover:bg-white/90 px-4"
          size="sm"
        >
          Instalar
        </Button>
      </div>
    </div>
  );
}

// Offline Banner - Shows when user is offline
export function PWAOfflineBanner() {
  const { isOffline } = usePWA();
  
  if (!isOffline) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bg-amber-500 text-white py-2 px-4 z-50 flex items-center justify-center gap-2 text-sm">
      <WifiOff className="w-4 h-4" />
      <span>Você está offline. Algumas funcionalidades podem estar limitadas.</span>
    </div>
  );
}

// Update Banner - Shows when a new version is available
// ATUALIZAÇÃO FORÇADA - Não pode ser descartada
export function PWAUpdateBanner() {
  const { hasUpdate, refresh } = usePWA();
  const [isUpdating, setIsUpdating] = useState(false);
  
  // Também verifica por versionamento do servidor
  const [serverUpdateAvailable, setServerUpdateAvailable] = useState(false);
  
  useEffect(() => {
    const checkServerVersion = async () => {
      try {
        const response = await fetch('/api/version', { 
          cache: 'no-store',
          headers: { 'Cache-Control': 'no-cache' }
        });
        if (response.ok) {
          const data = await response.json();
          const serverVersion = data.version;
          const storedVersion = localStorage.getItem('g2gente_app_version');
          
          if (storedVersion && storedVersion !== serverVersion) {
            console.log(`[PWA] Nova versão detectada: ${storedVersion} -> ${serverVersion}`);
            setServerUpdateAvailable(true);
          }
          localStorage.setItem('g2gente_app_version', serverVersion);
        }
      } catch (e) {
        console.log('[PWA] Erro ao verificar versão');
      }
    };
    
    checkServerVersion();
    // Verificar a cada 30 segundos
    const interval = setInterval(checkServerVersion, 30000);
    return () => clearInterval(interval);
  }, []);
  
  const handleForceUpdate = async () => {
    setIsUpdating(true);
    try {
      // Limpar todos os caches do navegador
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
        console.log('[PWA] Todos os caches limpos');
      }
      
      // Forçar unregister e re-register do service worker
      if ('serviceWorker' in navigator) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        await Promise.all(registrations.map(r => r.unregister()));
        console.log('[PWA] Service Workers removidos');
      }
      
      // Recarregar a página
      setTimeout(() => {
        window.location.reload(true);
      }, 300);
    } catch (error) {
      console.error('[PWA] Erro ao atualizar:', error);
      window.location.reload(true);
    }
  };
  
  if (!hasUpdate && !serverUpdateAvailable) return null;

  return (
    <div 
      data-testid="pwa-update-banner"
      className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-cyan-600 to-cyan-500 text-white py-4 px-4 z-[9999] shadow-lg"
      style={{ animation: 'slideUp 0.3s ease-out' }}
    >
      <style>
        {`
          @keyframes slideUp {
            from { transform: translateY(100%); }
            to { transform: translateY(0); }
          }
        `}
      </style>
      <div className="flex flex-col items-center justify-center gap-3 max-w-md mx-auto">
        <div className="flex items-center gap-2">
          <RefreshCw className={`w-5 h-5 ${isUpdating ? 'animate-spin' : ''}`} />
          <span className="font-semibold">Nova versão disponível!</span>
        </div>
        <Button 
          onClick={handleForceUpdate}
          disabled={isUpdating}
          className="bg-white text-cyan-600 hover:bg-white/90 px-6 font-semibold"
          size="sm"
          data-testid="pwa-update-btn"
        >
          {isUpdating ? 'Atualizando...' : 'Atualizar Agora'}
        </Button>
        <p className="text-xs text-white/80 text-center">
          Clique para obter as últimas funcionalidades e correções
        </p>
      </div>
    </div>
  );
}

// iOS Install Instructions - For Safari users
export function PWAiOSInstructions({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end">
      <div className="bg-white w-full rounded-t-3xl p-6 pb-10 animate-in slide-in-from-bottom">
        <button 
          onClick={onClose}
          className="absolute top-4 right-4 text-slate-400"
        >
          <X className="w-6 h-6" />
        </button>
        
        <h2 className="text-xl font-bold text-slate-900 mb-4">Instalar G2eGente</h2>
        
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0 text-cyan-600 font-bold">
              1
            </div>
            <div>
              <p className="font-medium text-slate-900">Toque no botão de compartilhar</p>
              <p className="text-sm text-slate-600">Na barra inferior do Safari</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0 text-cyan-600 font-bold">
              2
            </div>
            <div>
              <p className="font-medium text-slate-900">Role para baixo</p>
              <p className="text-sm text-slate-600">E toque em "Adicionar à Tela de Início"</p>
            </div>
          </div>
          
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0 text-cyan-600 font-bold">
              3
            </div>
            <div>
              <p className="font-medium text-slate-900">Confirme a instalação</p>
              <p className="text-sm text-slate-600">Toque em "Adicionar" no canto superior direito</p>
            </div>
          </div>
        </div>
        
        <Button 
          onClick={onClose}
          className="w-full mt-6 bg-cyan-600 hover:bg-cyan-700"
        >
          Entendi
        </Button>
      </div>
    </div>
  );
}

export default PWAInstallBanner;
